import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, photoUrl } from "../lib/api";
import { toast } from "sonner";
import { User, ArrowRight, Merge, Loader2, CheckCircle2, Eye, EyeOff, Sparkles } from "lucide-react";

const PREVIEW_FIELDS = [
  ["full_name", "Name"], ["nickname", "Nickname"], ["gender", "Gender"],
  ["birth_date", "Born"], ["death_date", "Died"],
  ["origin_place", "From"], ["current_place", "Lives"], ["bio", "Notes"],
];

// Simulate the backend merge: keep's non-empty fields win; blanks fill from the other record.
function buildPreview(keep, other) {
  return PREVIEW_FIELDS.map(([f, label]) => {
    const kv = keep[f], ov = other[f];
    const value = kv || ov || null;
    const from = kv ? "keep" : (ov ? "other" : null);
    return { field: f, label, value: value ? String(value) : null, filled: !kv && !!ov };
  }).filter((r) => r.value);
}

function confidenceOf(pair) {
  return pair.reason === "Identical name" || (pair.score || 0) >= 0.92 ? "likely" : "possible";
}

function ConfidenceBadge({ level }) {
  const likely = level === "likely";
  return (
    <span data-testid="dupe-confidence-badge"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${likely ? "bg-[#059669]/15 text-[#047857]" : "bg-[#D4A017]/20 text-[#8a6d10]"}`}>
      <Sparkles size={11} /> {likely ? "Likely match" : "Possible match"}
    </span>
  );
}

function PersonMini({ p, side, onKeep, keeping }) {
  return (
    <div className={`flex-1 rounded-xl border p-3 ${keeping ? "border-[#082D4A] bg-[#082D4A]/5" : "border-[#D8E0E8] bg-white"}`}>
      <div className="flex items-center gap-2">
        <span className="h-10 w-10 overflow-hidden rounded-full bg-[#E8EEF3]">
          {photoUrl(p.photo_path) ? <img src={photoUrl(p.photo_path)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-[#94A3B8]"><User size={16} /></span>}
        </span>
        <div className="min-w-0">
          <div className="truncate font-medium text-[#082D4A]">{p.full_name}</div>
          <div className="text-xs text-[#64748B]">
            {[p.birth_date, p.current_place].filter(Boolean).join(" · ") || "—"} · {p.rel_count} links
          </div>
        </div>
      </div>
      <Button size="sm" variant={keeping ? "default" : "outline"} onClick={onKeep} data-testid={`merge-keep-${side}`} className={`mt-2 w-full ${keeping ? "bg-[#082D4A] text-white" : ""}`}>
        {keeping ? <><CheckCircle2 size={14} className="mr-1" /> Keep this one</> : "Keep this one"}
      </Button>
    </div>
  );
}

function MergePreview({ keep, other }) {
  const rows = buildPreview(keep, other);
  const combinedLinks = Math.max(keep.rel_count || 0, other.rel_count || 0);
  return (
    <div className="mt-3 rounded-xl border border-[#082D4A]/20 bg-white p-3" data-testid="merge-preview-panel">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#082D4A]">
        <span className="h-8 w-8 overflow-hidden rounded-full bg-[#E8EEF3]">
          {photoUrl(keep.photo_path || other.photo_path) ? <img src={photoUrl(keep.photo_path || other.photo_path)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-[#94A3B8]"><User size={14} /></span>}
        </span>
        Surviving record after merge
      </div>
      <dl className="space-y-1">
        {rows.map((r) => (
          <div key={r.field} className="flex items-start gap-2 text-xs" data-testid={`merge-preview-${r.field}`}>
            <dt className="w-16 shrink-0 text-[#94A3B8]">{r.label}</dt>
            <dd className="flex-1 text-[#082D4A]">
              {r.value}
              {r.filled && <span className="ml-1.5 rounded-full bg-[#059669]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#047857]">filled in from the other</span>}
            </dd>
          </div>
        ))}
        <div className="flex items-start gap-2 text-xs">
          <dt className="w-16 shrink-0 text-[#94A3B8]">Links</dt>
          <dd className="flex-1 text-[#082D4A]">Relationships combined from both records (~{combinedLinks})</dd>
        </div>
      </dl>
    </div>
  );
}

export function DuplicatesDialog({ open, onOpenChange, onDone }) {
  const [loading, setLoading] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [keepChoice, setKeepChoice] = useState({}); // idx -> 'a' | 'b'
  const [merging, setMerging] = useState(null);
  const [previewOpen, setPreviewOpen] = useState({}); // idx -> bool

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPreviewOpen({});
    api.duplicates()
      .then((d) => { setPairs(d.pairs || []); const kc = {}; (d.pairs || []).forEach((_, i) => (kc[i] = "a")); setKeepChoice(kc); })
      .catch(() => toast.error("Could not scan for duplicates"))
      .finally(() => setLoading(false));
  }, [open]);

  const doMerge = async (pair, i) => {
    const keep = keepChoice[i] === "b" ? pair.b : pair.a;
    const other = keepChoice[i] === "b" ? pair.a : pair.b;
    setMerging(i);
    try {
      await api.merge(keep.id, other.id);
      toast.success(`Merged into ${keep.full_name}`);
      setPairs((ps) => ps.filter((_, idx) => idx !== i));
      onDone?.();
    } catch { toast.error("Merge failed"); }
    finally { setMerging(null); }
  };

  const mergeAll = async () => {
    if (!pairs.length) return;
    setMerging("all");
    let done = 0;
    try {
      for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i];
        const keep = keepChoice[i] === "b" ? pair.b : pair.a;
        const other = keepChoice[i] === "b" ? pair.a : pair.b;
        try { await api.merge(keep.id, other.id); done++; } catch { /* skip pairs that now conflict */ }
      }
      toast.success(`Merged ${done} ${done === 1 ? "pair" : "pairs"}`);
      setPairs([]);
      onDone?.();
    } finally { setMerging(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]">
            <Merge size={22} /> Possible duplicates
          </DialogTitle>
          <DialogDescription>
            People who look like the same person. Choose which record to keep — it absorbs the other's
            photo, dates, places, notes and relationships. Preview the result before you merge.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[#64748B]"><Loader2 className="animate-spin" size={18} /> Scanning…</div>
        ) : pairs.length === 0 ? (
          <div className="py-10 text-center text-[#64748B]" data-testid="no-duplicates">No likely duplicates found. 🎉</div>
        ) : (
          <div className="space-y-4 py-2">
            {pairs.length > 1 && (
              <div className="flex items-center justify-between rounded-xl border border-[#D8E0E8] bg-white p-2.5">
                <span className="text-sm text-[#64748B]">{pairs.length} possible duplicate pairs</span>
                <Button size="sm" onClick={mergeAll} disabled={merging !== null} data-testid="merge-all-button" className="bg-[#082D4A] text-white hover:bg-[#08223A]">
                  {merging === "all" ? <><Loader2 size={14} className="mr-1 animate-spin" /> Merging…</> : <><Merge size={14} className="mr-1" /> Merge all</>}
                </Button>
              </div>
            )}
            {pairs.map((pair, i) => {
              const keep = keepChoice[i] === "b" ? pair.b : pair.a;
              const other = keepChoice[i] === "b" ? pair.a : pair.b;
              return (
                <div key={`${pair.a.id}-${pair.b.id}`} data-testid={`duplicate-pair-${i}`} className="rounded-2xl border border-[#D8E0E8] bg-[#F4F6F8] p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#D4A017]">{pair.reason}</span>
                    <ConfidenceBadge level={confidenceOf(pair)} />
                  </div>
                  <div className="flex items-center gap-3">
                    <PersonMini p={pair.a} side="a" keeping={keepChoice[i] === "a"} onKeep={() => setKeepChoice((k) => ({ ...k, [i]: "a" }))} />
                    <ArrowRight size={18} className="shrink-0 text-[#94A3B8]" />
                    <PersonMini p={pair.b} side="b" keeping={keepChoice[i] === "b"} onKeep={() => setKeepChoice((k) => ({ ...k, [i]: "b" }))} />
                  </div>

                  <button type="button" onClick={() => setPreviewOpen((p) => ({ ...p, [i]: !p[i] }))} data-testid={`merge-preview-toggle-${i}`}
                    className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#082D4A] hover:underline">
                    {previewOpen[i] ? <EyeOff size={13} /> : <Eye size={13} />} {previewOpen[i] ? "Hide preview" : "Preview the merged result"}
                  </button>
                  {previewOpen[i] && <MergePreview keep={keep} other={other} />}

                  <Button onClick={() => doMerge(pair, i)} disabled={merging !== null} data-testid={`merge-confirm-${i}`} className="mt-3 w-full bg-[#D4A017] text-white hover:bg-[#B0850F]">
                    {merging === i ? "Merging…" : `Merge — keep ${keep.full_name}`}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
